import React, { useCallback, useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CheckIcon } from 'src/editor/shared/icons/check-icon'
import { Edit02Icon } from 'src/editor/shared/icons/edit-02-icon'
import { PlusCircleIcon } from 'src/editor/shared/icons/plus-circle-icon'
import { Trash01Icon } from 'src/editor/shared/icons/trash-01-icon'
import {
  ButtonSizeEnum,
  GhostIconButton,
  SecondaryButton,
} from 'src/editor/shared/ui-lib/buttons'
import { ColorCircle } from 'src/editor/shared/ui-lib/color-circle'
import { DEFAULT_COLOR } from 'src/editor/shared/ui-lib/color-picker'

const MAX_ALLOWED_COLORS = 8

export interface ColorPickerColorsProps {
  userColors: string[]
  editedColorIndex?: number
  isClearable: boolean
  handleEdit: (index: number) => void
  onSavedColorsChange: (colors: string[]) => void
  onClearColor: () => void
}

export const ColorPickerColors = ({
  userColors,
  editedColorIndex,
  handleEdit,
  onSavedColorsChange,
  isClearable,
  onClearColor,
}: ColorPickerColorsProps) => {
  const { t } = useTypedTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const handleAddColor = useCallback(() => {
    onSavedColorsChange([...userColors, DEFAULT_COLOR])
  }, [userColors, onSavedColorsChange])

  const handleDeleteColor = useCallback(
    (index: number) => {
      if (isEditing) {
        onSavedColorsChange(userColors.filter((_, i) => i !== index))
        if (userColors.length === 1) {
          setIsEditing(false)
        }
      }
    },
    [isEditing, userColors, onSavedColorsChange],
  )

  const handleColorBubbleClick = useCallback(
    (index: number) => {
      if (isEditing) {
        handleDeleteColor(index)
      } else {
        handleEdit(index)
      }
    },
    [isEditing, handleDeleteColor, handleEdit],
  )

  const handleToggleEditing = useCallback(() => {
    setIsEditing(wasEditing => !wasEditing)
  }, [])

  return (
    <div className="mt-2 flex flex-col gap-2">
      <div className="inline-flex items-center justify-between">
        <span className="text-S text-textTertiary">
          {t('components.color_picker.saved_colors')}
        </span>
        <GhostIconButton
          className="group"
          size={ButtonSizeEnum.Small}
          onClick={handleToggleEditing}
          icon={
            isEditing ? (
              <CheckIcon className="group-hover:stroke-btnPrimaryHover" />
            ) : (
              <Edit02Icon className="group-hover:stroke-btnPrimaryHover" />
            )
          }
        />
      </div>
      <div className="flex items-center gap-3">
        {userColors?.map((color, index) => (
          <ColorCircle
            // biome-ignore lint/suspicious/noArrayIndexKey: colors are not guaranteed to be unique
            key={index}
            isEditing={isEditing}
            color={color}
            isActive={index === editedColorIndex}
            onClick={() => handleColorBubbleClick(index)}
          />
        ))}
        {userColors.length < MAX_ALLOWED_COLORS && (
          <button
            type="button"
            className="inline-flex h-5 cursor-pointer items-center p-0"
            onClick={handleAddColor}
          >
            <PlusCircleIcon className="stroke-btnPrimary" />
          </button>
        )}
      </div>
      {isClearable && (
        <SecondaryButton
          className="mt-2!"
          size={ButtonSizeEnum.Small}
          iconBefore={<Trash01Icon className="fill-iconSecondary" />}
          onClick={onClearColor}
        >
          <span className="text-M text-textPrimary">
            {t('components.color_picker.clear_color')}
          </span>
        </SecondaryButton>
      )}
    </div>
  )
}
